import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Messaging } from '@angular/fire/messaging';
import { injectDeleteToken, injectSaveToken } from '@dataconnect/copilot/angular';
import { MessagePayload, getToken, onMessage } from 'firebase/messaging';
import { get, isEmpty, map, pull } from 'lodash-es';
import { ReplaySubject } from 'rxjs';
import { ENVIRONMENT } from '../../index';

const STORAGE_TOKEN_KEY = 'copilot:fcmToken';
const STORAGE_TOPICS_KEY = 'copilot:fcmTopics';

@Injectable({ providedIn: 'root' })
export class MessagingService {
  private messaging = inject(Messaging, { optional: true });
  private authService = inject(Auth);
  private platformId = inject(PLATFORM_ID);
  private environment = inject(ENVIRONMENT) as { firebase: { vapidKey: string } };
  private saveToken = injectSaveToken();
  private deleteToken = injectDeleteToken();


  private foregroundMessages$ = new ReplaySubject<MessagePayload>(1);
  private initialisationPromise?: Promise<void>;

  public messages$ = this.foregroundMessages$.asObservable();

  // istanbul ignore next
  async initialize(force: boolean): Promise<void> {
    if (this.initialisationPromise) {
      return this.initialisationPromise;
    }
    const topicsRaw = localStorage.getItem(STORAGE_TOPICS_KEY);
    const topics: string[] = topicsRaw ? JSON.parse(topicsRaw) : [];

    if (!force && isEmpty(topics)) {
      return;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('MessagingService: Service workers are not supported in this browser.');
      return;
    }

    if (!this.messaging) {
      console.warn('MessagingService: Firebase Messaging is not available.');
      return;
    }

    this.initialisationPromise = this.configureMessaging().catch((error) => {
      console.error('MessagingService: Failed to configure messaging.', error);
      throw error;
    });

    return this.initialisationPromise;
  }

  async subscribe(topic: string): Promise<void> {
    if (this.hasSubscription(topic)) {
      return;
    }
    const token = localStorage.getItem(STORAGE_TOKEN_KEY)!;
    const topics = this.getTopics();
    const uid = get(this.authService.currentUser, 'uid', 'unknown');
    const name = this.getName(get(navigator,'userAgentData', {}));
    await this.saveToken.mutateAsync({ organizationId: topic, uid, token, name });
    topics.push(topic);
    localStorage.setItem(STORAGE_TOPICS_KEY, JSON.stringify(topics));
  }

  async unsubscribe(topic: string): Promise<void> {
    if (!this.hasSubscription(topic)) {
      return;
    }
    const token = localStorage.getItem(STORAGE_TOKEN_KEY)!;
    const topics = this.getTopics();
    const uid = get(this.authService.currentUser, 'uid', 'unknown');
    await this.deleteToken.mutateAsync({ organizationId: topic, uid, token });
    pull(topics, topic);
    if (isEmpty(topics)) {
      // Reset the stored token and topics if no topics remain
      localStorage.removeItem(STORAGE_TOKEN_KEY);
      localStorage.removeItem(STORAGE_TOPICS_KEY);
    } else {
      localStorage.setItem(STORAGE_TOPICS_KEY, JSON.stringify(topics));
    }
  }

  public hasSubscription(organizationId: string): boolean {
    const token = localStorage.getItem(STORAGE_TOKEN_KEY);
    const topics = this.getTopics();
    return !!token && topics.includes(organizationId);
  }

  // istanbul ignore next
  private async configureMessaging(): Promise<void> {
    const registration = await this.getServiceWorkerRegistration();

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('MessagingService: Notification permission was not granted.');
      return;
    }

    const token = await getToken(this.messaging as Messaging, {
      vapidKey: this.environment.firebase.vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      console.warn('MessagingService: Failed to obtain an FCM registration token.');
      return;
    }

    await this.registerTokenIfNeeded(token);

    onMessage(this.messaging as Messaging, (payload) => {
      this.foregroundMessages$.next(payload);

      if (document.visibilityState === 'visible' && payload.notification?.title && Notification.permission === 'granted') {
        new Notification(payload.notification.title, {
          body: payload.notification.body ?? undefined,
          data: payload.data,
        });
      }
    });
  }

  // istanbul ignore next
  private async getServiceWorkerRegistration(serviceWorkerPath?: string): Promise<ServiceWorkerRegistration> {
    const swPath = serviceWorkerPath ?? '/firebase-messaging-sw.js';
    const existingRegistration = await navigator.serviceWorker.getRegistration(swPath);

    if (existingRegistration) {
      return existingRegistration;
    }

    return navigator.serviceWorker.register(swPath);
  }

  // istanbul ignore next
  private async registerTokenIfNeeded(token: string): Promise<void> {
    const cachedToken = localStorage.getItem(STORAGE_TOKEN_KEY);

    const tokenChanged = token !== cachedToken;

    if (!tokenChanged) {
      return;
    }

    const topics = this.getTopics();

    // Token changed, unregister from old token, add to new token
    if (!isEmpty(topics)) {
      const uid = get(this.authService.currentUser, 'uid', 'unknown');
      const name = this.getName(get(navigator,'userAgentData', {}));
      for (const topic of topics) {
        if (cachedToken) {
          await this.deleteToken.mutateAsync({ organizationId: topic, uid, token: cachedToken });
        }
        await this.saveToken.mutateAsync({ organizationId: topic, uid, token, name });
      }
    }
    localStorage.setItem(STORAGE_TOKEN_KEY, token);
  }

  private getTopics(): string[] {
    const topicsRaw = localStorage.getItem(STORAGE_TOPICS_KEY);
    return topicsRaw ? JSON.parse(topicsRaw) : [];
  }

  private getName(userAgentData: { brands?: { brand: string; version: string }[]; platform?: string }): string {
    return `${get(userAgentData, 'platform', 'unknown')} - ${map(get(userAgentData, 'brands', []), 'brand').join(', ')}`;
  }
}
